export function createCameraInterruptionController({
  now = Date.now,
  onState = () => {},
  recover = async () => false
} = {}) {
  let state = {
    status: 'live',
    interruptedAt: null,
    totalInterruptedMs: 0,
    recoveryAttempts: 0,
    message: ''
  };
  let recoveryPromise = null;

  function publish(patch) {
    state = { ...state, ...patch };
    onState({ ...state });
    return state;
  }

  function handleMute() {
    if (state.status === 'interrupted' || state.status === 'recovering' || state.status === 'failed') {
      return state;
    }
    return publish({
      status: 'interrupted',
      interruptedAt: now(),
      message: 'Camera video was interrupted. Audio may still be recording.'
    });
  }

  function handleUnmute() {
    if (state.status !== 'interrupted') return state;
    const resumedAt = now();
    const interruptedMs = Math.max(0, resumedAt - (Number(state.interruptedAt) || resumedAt));
    return publish({
      status: 'live',
      interruptedAt: null,
      totalInterruptedMs: state.totalInterruptedMs + interruptedMs,
      message: interruptedMs > 0
        ? `Camera recovered after ${(interruptedMs / 1000).toFixed(1)} seconds.`
        : 'Camera recovered.'
    });
  }

  function handleEnded() {
    if (recoveryPromise) return recoveryPromise;
    publish({
      status: 'recovering',
      interruptedAt: state.interruptedAt ?? now(),
      recoveryAttempts: state.recoveryAttempts + 1,
      message: 'Camera stopped. Attempting one safe recovery.'
    });

    let recoveryResult;
    try {
      recoveryResult = recover();
    } catch (error) {
      recoveryResult = Promise.reject(error);
    }
    recoveryPromise = Promise.resolve(recoveryResult)
      .then(recovered => {
        if (recovered) {
          const recoveredAt = now();
          const interruptedMs = Math.max(
            0,
            recoveredAt - (Number(state.interruptedAt) || recoveredAt)
          );
          publish({
            status: 'live',
            interruptedAt: null,
            totalInterruptedMs: state.totalInterruptedMs + interruptedMs,
            message: 'Camera recovered successfully.'
          });
          return true;
        }
        publish({
          status: 'failed',
          message: 'Camera could not be recovered. Recording will be finalized safely.'
        });
        return false;
      })
      .catch(error => {
        publish({
          status: 'failed',
          message: error?.message || 'Camera recovery failed. Recording will be finalized safely.'
        });
        return false;
      })
      .finally(() => {
        recoveryPromise = null;
      });

    return recoveryPromise;
  }

  function handleRecorderError(error) {
    return publish({
      status: 'failed',
      message: error?.message || 'The video recorder stopped unexpectedly.'
    });
  }

  function reset() {
    recoveryPromise = null;
    return publish({
      status: 'live',
      interruptedAt: null,
      totalInterruptedMs: 0,
      recoveryAttempts: 0,
      message: ''
    });
  }

  return {
    getState: () => ({ ...state }),
    handleMute,
    handleUnmute,
    handleEnded,
    handleRecorderError,
    reset
  };
}
